import * as Sentry from '@sentry/react'
import { requiredError } from 'Components/Forms/FormErrors'
import { AlertComponent } from 'Components/HelperComponents/AlertComponent'
import LoadingComponent from 'Components/HelperComponents/LoadingComponent'
import { EditComponent } from 'NewVersion/components/Fields/EditComponent'
import { fetchGuillotina } from 'NewVersion/services/guillotina'
import { useFormik } from 'formik'
import { useEdition } from 'hooks/useEdition'
import { useGuillotina } from 'hooks/useGuillotina'
import { useScrollToFirstError } from 'hooks/useScrollToFirstError'
import React, { Fragment } from 'react'
import { useTranslation, withTranslation } from 'react-i18next'
import { get } from 'utils/objectUtils'
import { getCurrentJWToken } from 'utils/utils'
import Button from '../UI/Buttons/Button'

const FormContent = ({ setRepresentantObj, ensTypeObj, toggleInit, showDetailObj }) => {
  const { edition, currentEdition, loading } = useEdition()

  const {
    data: entitySchemaData,
    isLoading: entitySchemaLoading,
    error: entitySchemaError,
  } = useGuillotina({
    path: `@types/${ensTypeObj['interface']}`,
  })

  if (loading || entitySchemaLoading) {
    return <LoadingComponent />
  }

  return (
    <ManagerFormContent
      ensTypeObj={ensTypeObj}
      edition={edition}
      toggleInit={toggleInit}
      currentEdition={currentEdition}
      schema={{
        data: entitySchemaData,
        error: entitySchemaError,
        loading: entitySchemaLoading,
      }}
      setRepresentantObjManagerForm={setRepresentantObj}
      showDetailObj={showDetailObj}
    />
  )
}

const ManagerFormContent = ({
  ensTypeObj,
  edition,
  toggleInit,
  schema,
  readOnlyForm = false,
  showDetailObj,
  setRepresentantObjManagerForm,
}) => {
  const { t } = useTranslation()
  const [submitError, setSubmitError] = React.useState({
    hasError: false,
    message: '',
  })

  const handleFormError = (error) => {
    const err = new Error(`Manager form error: ${JSON.stringify(get(error, 'info', error))}`)
    err.name = 'Manager form error'
    Sentry.captureException(err)
    if (error.response && error.response.status === 412) {
      if (get(error, 'info.code') === 'already-exists') {
        setSubmitError({
          hasError: true,
          message: `Error: ${t('manager_title_already_exists')}`,
        })
      } else if (get(error, 'info.code') === 'invalid-email') {
        setSubmitError({
          hasError: true,
          message: `Error: ${t('invalid_email')}`,
        })
      } else if (get(error, 'info.code') === 'promo-code-not-exist') {
        setSubmitError({
          hasError: true,
          message: `Error: ${t('promo_code_not_exist')}`,
        })
      } else {
        setSubmitError({
          hasError: true,
          message: `Error: ${t('error_create_or_update_ens')}`,
        })
      }
    } else {
      setSubmitError({
        hasError: true,
        message: `Error: ${t('error_generic')}`,
      })
    }
    window.scrollTo(0, 0)
  }

  const fetchCreatedRepresentant = (representantPath) => {
    const token = getCurrentJWToken()
    fetchGuillotina({
      path: representantPath,
      method: 'GET',
      token,
    })
      .then((response) => {
        setRepresentantObjManagerForm(representantPath, response)
      })
      .catch((error) => {
        handleFormError(error)
      })
  }

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true)
    const valuesEntity = {}
    const token = getCurrentJWToken()
    Object.keys(values).forEach((valueKey) => {
      if (valueKey.indexOf('entity_') === 0) {
        valuesEntity[valueKey.replace('entity_', '')] = values[valueKey] || null
      }
    })

    const data = Object.assign({}, valuesEntity, {
      '@type': ensTypeObj['interface'],
      tipus: 'representant',
      tarifa: 'gratuita',
    })

    if (showDetailObj) {
      await fetchGuillotina({
        path: `${edition}`,
        method: 'PATCH',
        data: data,
        token: token,
      })
        .then(() => {
          toggleInit()
        })
        .catch((error) => {
          handleFormError(error)
          setSubmitting(false)
          return
        })
        .finally(() => {
          setSubmitting(false)
        })
    } else {
      await fetchGuillotina({
        path: `${edition}`,
        method: 'POST',
        data: data,
        token: token,
      })
        .then(async (response) => {
          if (response) {
            const path = '/' + response['@id'].split('/').slice(-2).join('/')
            fetchCreatedRepresentant(path)
          }
          setSubmitting(false)
        })
        .catch((error) => {
          handleFormError(error)
          setSubmitting(false)
          return
        })
    }
  }

  const getInitialValues = React.useMemo(() => {
    const result = {}
    ensTypeObj['requiredFields'].forEach((item) => {
      result[`entity_${item}`] = showDetailObj ? showDetailObj.item : undefined
    })
    return result
  }, [ensTypeObj])

  const formik = useFormik({
    initialValues: getInitialValues,
    onSubmit: handleSubmit,
    enableReinitialize: true,
    validate: (values) => {
      const errors = {}
      ensTypeObj['requiredFields'].forEach((key) => {
        if (key !== 'declaracio_acceptar') {
          const keyForm = `entity_${key}`
          let value = get(values, keyForm, null)
          if (typeof value === 'string') {
            value = value.trim()
          }
          if ((!(keyForm in values) || !value) && renderLocationFields(key)) {
            errors[keyForm] = requiredError()
          }
        }
      })

      const keyForm = `entity_correu`
      let value = get(values, keyForm, null)
      if (typeof value === 'string') {
        value = value.trim()
      }
      if (value && !value.match(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i)) {
        errors[keyForm] = t('invalid_email')
      }
      return errors
    },
  })
  useScrollToFirstError(formik.errors, formik.submitCount)

  const fieldsWithPrefixLabel = ['idioma', 'correu', 'telefon', 'mobil']

  const getLabel = (key) => {
    if (fieldsWithPrefixLabel.includes(key)) return `ens_${key}`
    return key
  }

  const renderLocationFields = (key) => {
    if (key === 'comunitat_autonoma' && get(formik, 'values.entity_pais', '') !== 'country-ES') {
      return false
    }
    if (
      key === 'comarca' &&
      (get(formik, 'values.entity_pais', '') !== 'country-ES' ||
        get(formik, 'values.entity_comunitat_autonoma', '') !== 'catalunya')
    ) {
      return false
    }
    return true
  }
  const renderFields = (key) => {
    if (key === 'declaracio_acceptar' || key === 'representant') return false
    return renderLocationFields(key)
  }

  const getDescription = (value, key) => {
    /* TODO: Refactor de les descripcions dels camps, evitar casos unics i utilitzar el type_name per crear les claus,
    es necessari modificar back per retornar la clau en el camp descripcio per exemple, analitzar-ho bé. */

    if (key === 'correu') {
      return t('ens_correu_description')
    } else if (key === 'web') {
      return t('web_description')
    } else if (key === 'facebook') {
      return t('facebook_description')
    } else if (key === 'twitter') {
      return t('twitter_description')
    } else if (key === 'instagram') {
      return t('instagram_description')
    } else if (key === 'linkedin') {
      return t('linkedin_description')
    } else if (key === 'youtube') {
      return t('youtube_description')
    }
    return value.description ? t(`${key}_description`) : null
  }

  const renderField = (key) => {
    if (renderFields(key) && get(schema, `data.properties[${key}]`, null)) {
      const value = schema.data.properties[key]
      const keyForm = `entity_${key}`
      return (
        <EditComponent
          key={keyForm}
          name={keyForm}
          label={key === 'title' ? t(getLabel(ensTypeObj['labelName'])) : t(getLabel(key))}
          schema={value}
          description={getDescription(value, key)}
          value={formik.values[keyForm]}
          required={(ensTypeObj['requiredFields'] ?? []).includes(key)}
          onBlur={() => {
            formik.setFieldTouched(keyForm)
          }}
          onChange={(ev) => {
            formik.setFieldValue(keyForm, ev)
            formik.validateForm()
          }}
          error={formik.touched[keyForm] && formik.errors[keyForm]}
          dataTest={`inputFieldTest_${keyForm}`}
        />
      )
    }
  }

  const renderColumns = (item) => {
    const middleIndex = Math.ceil(item['fields'].length / 2)
    return (
      <div className="fragment">
        <div className="fragmentColumn">
          {item['fields'].slice(0, middleIndex).map((key) => renderField(key))}
        </div>
        <div className="fragmentColumn">
          {item['fields'].slice(middleIndex).map((key) => renderField(key))}
        </div>
      </div>
    )
  }

  return (
    <>
      {submitError.hasError && submitError.message && (
        <AlertComponent type="danger"> {submitError.message}</AlertComponent>
      )}

      <h4>
        {t('create_new_representant_profile')} {edition}
      </h4>

      <form
        className="Form"
        onSubmit={(values) => formik.handleSubmit(values)}
        data-test="formTest"
      >
        {schema &&
          schema.data &&
          !schema.loading &&
          ensTypeObj['orderFieldsForm']
            .filter((item) => item.title !== 'declaracio_fieldset')
            .map((item) => {
              return (
                <Fragment key={item['title']}>
                  {item['title'] !== 'entity_data' && <h5>{t(item['title'])}&nbsp;</h5>}
                  {renderColumns(item)}
                </Fragment>
              )
            })}

        <div>
          {formik.isSubmitting && <LoadingComponent />}
          {!readOnlyForm && (
            <div className="footer-buttons">
              <Button
                type="button"
                dataTest="btnCancelFormTest"
                disabled={formik.isSubmitting}
                onClick={() => toggleInit()}
                color="primaryOutlinedIcon outlined"
              >
                {t('cancel')}
              </Button>
              <Button
                style={{ width: '130px' }}
                type="submit"
                dataTest="btnSubmitFormTest"
                disabled={formik.isSubmitting}
              >
                {t('next')}
              </Button>
            </div>
          )}
        </div>
      </form>
    </>
  )
}

export const ManagerForm = withTranslation()(FormContent)
